<script context="module">
    // Licensed under Gnu Public License version 2
    // Copyright (c) 2023-2024 (original work) Open Assessment Technologies SA ;

    /**
     * The minimal size to give to a calculator's container.
     * The size is given in `rem`.
     * @type {object}
     */
    export const calculatorMinSize = {
        width: 40,
        height: 72
    };
</script>

<script>
    import { __, generateElementId } from '@oat-sa-private/ui-core';
    import { createEventDispatcher } from 'svelte';
    import { engineFactory, expressionHelper, historyPlugin, terms, tokensHelper } from '@oat-sa/tao-calculator';
    import { getLabelType, getLabelValue, getAriaLabelValue, addAriaLabelsToTerms } from './labels.js';
    import * as keyboards from './keyboards';
    import MathExpressionTerm from './MathExpressionTerm.svelte';
    import Icon from '@oat-sa-private/ui-elements/icon/Icon.svelte';
    import RadioSwitch from '@oat-sa-private/ui-elements/radioSwitch/RadioSwitch.svelte';

    /**
     * A component that displays a calculator.
     * @property {string} type the type of calculator to present, it can be either: 'basic', 'bodmas', 'scientific'
     * @property {string} expression the expression being shown in the calculator and that will be evaluated
     * @property {number} decimals the number of decimals to present after the dot
     * @property {boolean} instant activate the instant computation mode
     * @property {boolean} corrector activate the auto-corrector for unclosed expressions
     * @fires 'command' when a command is invoked in the calculator
     * @fires 'change' when the expression is changed
     * @fires 'result' when the expression has been evaluated
     * @fires 'error' when an error occurred while evaluating the expression
     */

    export let type = 'basic';
    export let expression = '';
    export let decimals = 5;
    export let instant = type === 'basic';
    export let corrector = true;

    const dispatch = createEventDispatcher();

    // Keyboards are indexed by calculator's type.
    // Each of them is made of a list of rows containing buttons.
    let keyboard;
    //array of buttons with grid placement info, sorted by tabindex
    let keyboardButtons;

    $: {
        keyboard = keyboards[type];
        keyboardButtons = getKeyboardButtons(keyboard);
    }

    // Calculator's engine. It will be responsible of managing the maths expression and evaluating it.
    const plugins = { history: historyPlugin };
    const calculator = engineFactory({ instant, corrector, plugins });
    const lastResultVariable = terms.VAR_ANS.value;
    const errorValue = terms.ERROR.value;

    const resultLabel = __('the result is');
    const emptyInputFieldLabel = __('Input area');

    const resultAnnouncementTimeoutMS = 300;

    // State variables utilized for rendering the expression and its result.
    let resultValue = '';
    let renderedTerms = [];
    let formattedResult = [];

    // Edition state for the expression.
    // When active, any command will apply to the current expression.
    // When not active, any command will start a new expression.
    let active = true;

    // Turns on/off the error mode.
    // All errors reported by the component are recoverable and need the user's attention since it relates to the expression being composed.
    // The engine reports 3 kinds of errors related to the expression:
    // - syntax (incomplete or bad expression)
    // - infinity (domain overflow or divide by 0)
    // - error (divide by 0 or not number result)
    let error = false;

    //stores reference to keyboard button which is currently operated by keyboard
    let currentKeyboardButton;

    //keydown state
    let keyButtonPressed = false;

    //calculator container svelte binding
    let calculatorScreen;

    //result container svelte binding
    let resultContainer;

    //live regions svelte binding
    let assertiveLiveRegion;
    let politeLiveRegion;

    //focus screen after screen is bound to variable
    $: {
        if (calculatorScreen) {
            calculatorScreen.focus();
        }
    }

    //parameter to detect first focus of the screen
    let mountfocus = true;

    //calculator keyboard svelte binding
    let calculatorKeyboard;

    //some key shortcuts will be several character long
    let keyBuffer = '';

    //timeout to reset buffer
    let keyBufferTimeoutId;

    //value in ms before buffer resets
    const keyBufferTTL = 1000;

    let switchValues = {
        degreeMode: calculator.isDegreeMode().toString()
    };

    const expressionContainerId = generateElementId('expressionContainer');

    /**
     * Renders an expression, rounding and formatting the value with respect to the config.
     * @param {mathsExpression} expr
     * @returns {renderTerm[]}
     */
    function formatExpression(expr) {
        const variables = expressionHelper.roundAllVariables(calculator.getAllVariables(), decimals);
        return expressionHelper.nestExponents(expressionHelper.render(expr, variables, calculator.getTokenizer()));
    }

    /**
     * Executes action assigned to keyboard button
     * @param {DOMElement} button
     */
    function handleButtonActivation(button) {
        const { command, param } = button.dataset;
        calculator.invoke(command, param);

        dispatch('command', { command, param });
    }

    /**
     * Captures a click on the calculator's keyboard
     * @param {Event} e
     */
    function keyboardClick(e) {
        const button = e.target.closest('[data-command]');
        if (!button) {
            return;
        }
        handleButtonActivation(button);
    }

    /**
     * Handler for keyboard keydown event
     * This would come from contained buttons
     * @param {Event} e
     */
    function handleKeyboardKeydown(e) {
        const button = e.target.closest('[data-command]');
        if (button && (e.key.toLowerCase() === 'enter' || e.key.toLowerCase() === ' ')) {
            handleButtonActivation(button);
            //prevent button action keys from bubbling to calculator which can have
            //'enter' and 'space' keyboard shortcut assignments
            e.stopImmediatePropagation();
            e.preventDefault();
        }
    }

    /**
     * Searches keyboard button by key
     * @param {string} key
     * @returns {keyboardButton} button from keyboardDefinition
     */
    function findKeyboardButtonByKey(key) {
        let foundButton;
        keyboard.find(row =>
            row.find(button => {
                if (button.keys && button.keys.includes(key.toLowerCase())) {
                    foundButton = button;
                    return true;
                } else {
                    return false;
                }
            })
        );
        return foundButton;
    }

    /**
     * Handles keydown event of the calculator container
     * @param {Event} e
     */
    function handleKeydown(e) {
        if (!keyButtonPressed) {
            //special case applicable to all calculator types
            if (e.key.toLowerCase() === 'delete' || e.key.toLowerCase() === 'backspace') {
                const command = 'deleteLeft';
                calculator.invoke(command);
                dispatch('command', {
                    command
                });
            } else {
                //buffer only single chars
                if (e.key.length === 1) {
                    bufferKey(e.key.toLowerCase());
                } else {
                    resetBuffer();
                }

                currentKeyboardButton = findKeyboardButtonByKey(e.key) || findKeyboardButtonByKey(keyBuffer);

                if (currentKeyboardButton) {
                    //set the keydown state to disable multi-keys
                    keyButtonPressed = true;
                    //highlight pressed key
                    setKeyPressedClass(currentKeyboardButton, true);
                }
            }
        }
    }

    /**
     * Handles keyup event of the calculator container
     * @param {Event} e
     */
    function handleKeyup(e) {
        mountfocus = false;
        if (
            currentKeyboardButton &&
            currentKeyboardButton.keys &&
            (currentKeyboardButton.keys.includes(e.key.toLowerCase()) ||
                currentKeyboardButton.keys.includes(keyBuffer.toLowerCase()))
        ) {
            //reset keyButtonPressed state and buffer
            keyButtonPressed = false;
            resetBuffer();

            setKeyPressedClass(currentKeyboardButton, false);

            const { command, parameter: param } = currentKeyboardButton;
            calculator.invoke(command, param);
            dispatch('command', {
                command,
                param
            });
            currentKeyboardButton = null;
        }
    }

    /**
     * Handler for switches
     * @param {string} name name for the switches
     * @param {CustomEvent} e switch change event
     */
    function switchHandler(name, e) {
        if (name === 'degreeMode') {
            switchValues['degreeMode'] = e.detail.value;
            calculator.setDegreeMode(switchValues['degreeMode'] === 'true' ? true : false);
        }
    }

    /**
     * Sets pressed class for the keyboard key button
     * @param {keyboardButton} keyboardButton the button to set pressed class on
     * @param {boolean} pressed pressed value
     */
    function setKeyPressedClass(keyboardButton, pressed) {
        const keyboardButtonElement = calculatorKeyboard.querySelector(
            `[data-keys='${getCompositeKey(keyboardButton.keys)}']`
        );
        if (keyboardButtonElement) {
            if (pressed) {
                keyboardButtonElement.classList.add('pressed');
            } else {
                keyboardButtonElement.classList.remove('pressed');
            }
        }
    }

    /**
     * Creates array of buttons sorted by tabindex
     * @param {keyboardDefinition} keyboardDefinition
     * @returns {keyboardButton[]} sorted array of button tabindex
     */
    function getKeyboardButtons(keyboardDefinition) {
        const orderedKeyboardButtons = [];
        keyboardDefinition.forEach((row, rowNum) => {
            let colNum = 0;
            row.forEach(button => {
                const keyboardButton = Object.assign({}, button, { rowNum, colNum });
                colNum += button.size;
                orderedKeyboardButtons.push(keyboardButton);
            });
        });
        orderedKeyboardButtons.sort((a, b) => a.tabindex - b.tabindex);
        return orderedKeyboardButtons;
    }

    /**
     * Creates composite key from keys array
     * @param {string[]} keys
     * @returns {string} composite key
     */
    function getCompositeKey(keys) {
        let compositeKey;
        if (keys && keys.length) {
            compositeKey = keys.join('|');
        }
        return compositeKey;
    }

    /**
     * Generates grid style for button positioning
     * @param {number} rowNum index of button row
     * @param {number} colNum index of button start column
     * @param {number} size grid size of the button
     * @returns {string} button grid position style
     */
    function getButtonGridPositionStyle(rowNum, colNum, size) {
        return `grid-column: ${colNum + 1} / span ${size}; grid-row: ${rowNum + 1} / span 1`;
    }

    /**
     * Manages key buffer
     * @param {string} key key to add to buffer
     */
    function bufferKey(key) {
        keyBuffer += key;
        if (keyBufferTimeoutId) {
            clearTimeout(keyBufferTimeoutId);
        }
        keyBufferTimeoutId = setTimeout(resetBuffer, keyBufferTTL);
    }

    /**
     * Resets keyBuffer
     */
    function resetBuffer() {
        keyBuffer = '';
        if (keyBufferTimeoutId) {
            clearTimeout(keyBufferTimeoutId);
        }
        keyBufferTimeoutId = void 0;
    }

    /**
     * Maps switch button states to RadioSwitch options
     * @param {keyboardSwitchState[]} states
     * @returns {radioOptionDescriptor}
     */
    function getSwitchOptions(states) {
        return states.map(state => {
            state.value = state.value.toString();
            return Object.assign({}, state, { value: state.value.toString() });
        });
    }

    /**
     * @typedef {object} term - Represents a tokenizable term
     * @property {string} label - The displayable text
     * @property {string} value - The related text that should be found in the expression
     * @property {string} type - The type of token
     * @property {string|boolean} exponent - Some terms introduce exponent notation, and this property tells on which side
     */

    /**
     * Creates array where all digit terms representing numbers are combined together
     * @param {term[]} expressionTerms
     * @returns {term[]} terms array with numbers combined
     */
    function combineDigitTerms(expressionTerms) {
        return expressionTerms.reduce((acc, current) => {
            if (Array.isArray(current.label)) {
                current.label = combineDigitTerms(current.label);
            }
            if (acc.length && acc[acc.length - 1].type === 'digit' && current.type === 'digit') {
                const lastDigit = Object.assign({}, acc[acc.length - 1]);
                lastDigit.value += current.value;
                lastDigit.label += current.label;
                lastDigit.token = void 0;
                acc[acc.length - 1] = lastDigit;
            } else {
                acc.push(current);
            }
            return acc;
        }, []);
    }

    /**
     * Factory to create element updater
     * @param {number} timeout throttling timeout in milliseconds
     * @returns {Function} update function
     */
    function throttleUpdate(timeout) {
        let timeoutId;
        return element => {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
            timeoutId = setTimeout(() => {
                if (element && resultContainer) {
                    //add or remove dummy string to make browser believe it should read the result as a whole
                    //this trick doesn't work with Chrome+VoiceOver though. The only updated part is announced
                    if (element.innerHTML.startsWith('&nbsp;')) {
                        element.innerHTML = resultContainer?.innerHTML;
                    } else {
                        element.innerHTML = `&nbsp;${resultContainer?.innerHTML}`;
                    }
                }
            }, timeout);
        };
    }

    const throttleLiveRegionUpdate = throttleUpdate(resultAnnouncementTimeoutMS);

    calculator
        .on('expression', expr => {
            expression = expr;
            renderedTerms = expressionHelper.nestExponents(calculator.render(decimals));
            dispatch('change', { expression });
        })
        .on('result', result => {
            resultValue = result.result;
            active = false;
            error = calculator.error;

            if (error) {
                formattedResult = formatExpression(result);

                dispatch('error', { error: resultValue });
            } else {
                formattedResult = formatExpression(lastResultVariable);

                dispatch('result', { result });
            }

            throttleLiveRegionUpdate(politeLiveRegion);
        })
        .on('command', (name, parameter) => {
            if (active || error) {
                return;
            }

            if (calculator.isInstantMode()) {
                if (name === 'execute') {
                    calculator.replace(lastResultVariable);
                }
                return;
            }

            // The expression is inactive.
            // The result was just calculated, any command invoked now would start a new expression.
            let expr = '';

            if (name === 'term') {
                // If the invoked command introduces an operator, we want to apply it on the last result.
                const [token] = parameter.split(/\s+/);
                if (tokensHelper.isOperator(terms[token])) {
                    expr = lastResultVariable;
                }
            }

            calculator.replace(expr);
        })
        .on('command clear', () => {
            resultValue = '';
            active = true;
            error = false;
        })
        .on('syntaxerror', () => {
            resultValue = errorValue;
            active = false;
            error = true;

            formattedResult = formatExpression(resultValue);

            throttleLiveRegionUpdate(assertiveLiveRegion);

            dispatch('error', { error: 'Syntax' });
        })
        // Logs the engine's errors. They relate to the API use and need the developer's attention as they reflect a bad implementation:
        // - unknown variable
        // - unknown command
        // - unknown term
        // eslint-disable-next-line no-console
        .on('error', e => window.console.error(e));

    // react to modification from the outside
    $: {
        if (calculator.getExpression() !== expression) {
            calculator.replace(expression);
        }

        if (calculator.isInstantMode() !== instant) {
            calculator.setInstantMode(instant);
        }

        if (calculator.isCorrectorMode() !== corrector) {
            calculator.setCorrectorMode(corrector);
        }
    }
</script>

<style>
    .calculator {
        @define-mixin digit-highlighted {
            color: var(--keyboard-digit-text-hover);
            background-color: var(--keyboard-digit-background-hover);
        }
        @define-mixin operator-highlighted {
            color: var(--keyboard-digit-text-hover);
            background-color: var(--keyboard-digit-background-hover);
        }
        @define-mixin key-highlighted {
            color: var(--keyboard-command-text-hover);
            background-color: var(--keyboard-command-background-hover);
        }

        display: flex;
        flex-direction: column;
        align-items: stretch;
        align-content: stretch;
        height: 100%;
        outline: 0;
        --dir-typography-font: var(--default-font-ui);

        --calculator-size-unit: 3rem;
        --calculator-button-margin: 0.125rem;

        --error-text: var(--color-text-inverted);
        --error-background: var(--color-bg-inverted-secondary);

        --screen-text: var(--color-text-inverted);
        --screen-background: var(--color-bg-inverted-secondary);
        --screen-background-light: var(--color-bg-inverted-secondary-hover);
        --screen-separator: var(--color-border-inverted);

        --keyboard-command-text: var(--color-text-default);
        --keyboard-command-background: var(--color-gs-light-alternative-bg);
        --keyboard-command-text-hover: var(--color-text-default);
        --keyboard-command-background-hover: var(--color-gs-light-hover-bg);

        --keyboard-operator-text: var(--color-text-inverted);
        --keyboard-operator-background: var(--color-brand);
        --keyboard-operator-text-hover: var(--color-text-inverted);
        --keyboard-operator-background-hover: var(--color-brand-hover);

        --keyboard-digit-text: var(--color-text-default);
        --keyboard-digit-background: var(--color-brand-light);
        --keyboard-digit-text-hover: var(--color-text-inverted);
        --keyboard-digit-background-hover: var(--color-brand-hover);

        &.basic {
            & .result {
                font-size: 4.8rem;
                min-height: 7.2rem;
            }
        }
        &:not(.basic) {
            & .result {
                border-top: var(--border-thin) solid var(--screen-separator);
                padding-top: 1rem;
                & span {
                    background-color: var(--screen-background-light);
                }
            }
        }
    }
    .screen {
        &:not(.mountfocus) {
            @add-mixin outline-focus 0;
            &:focus-visible::after {
                border-color: var(--color-border-focus-inverted);
            }
        }
        outline: 0;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: flex-end;
        padding: 1.1rem 1.6rem;
        gap: 0.4rem;
        color: var(--screen-text);
        background-color: var(--screen-background);
        flex: 0 0 auto;
        min-width: calc(12 * var(--calculator-size-unit));
    }
    .expression {
        display: flex;
        flex-direction: column;
        justify-content: flex-end;
        text-align: right;
        font-weight: 400;
        font-size: 1.6rem;
        line-height: 2.1rem;
        min-height: 4.2rem;
        width: 100%;

        &:not(.active) {
            opacity: 0.6;
        }
    }
    .result {
        text-align: right;
        font-weight: 700;
        font-size: 2.4rem;
        line-height: 1.5;
        min-height: 5rem;
        width: 100%;
    }
    .error {
        color: var(--error-text);
        background-color: var(--error-background);
    }
    .keyboard {
        display: grid;
        grid-template-rows: repeat(var(--grid-rows-count), 1fr);
        grid-template-columns: repeat(12, 1fr);
        height: 100%;
        width: 100%;
    }
    .key,
    .spacer,
    .switchContainer {
        margin: var(--calculator-button-margin);
        min-height: 4rem;
        font-size: 2rem;
        display: inline-block;
        border: 0;
    }
    .key {
        @add-mixin outline-focus 0;
        cursor: pointer;
        color: var(--keyboard-command-text);
        background-color: var(--keyboard-command-background);
        &:hover,
        &.pressed {
            @add-mixin key-highlighted;
        }
    }
    .switchContainer {
        display: flex;
        padding: 1rem;
        justify-content: flex-start;
        align-items: center;
    }

    .function {
        font-size: 1.65rem;
    }
    .command {
        font-weight: bold;
    }
    .operator {
        color: var(--keyboard-operator-text);
        background-color: var(--keyboard-operator-background);
        &:hover,
        &.pressed {
            @add-mixin operator-highlighted;
        }
        &:focus-visible::after {
            border-color: var(--color-border-focus-inverted);
        }
    }
    .digit {
        color: var(--keyboard-digit-text);
        background-color: var(--keyboard-digit-background);
        font-weight: bold;
        &:hover,
        &.pressed {
            @add-mixin digit-highlighted;
        }
    }

    .empty-input-hint,
    .live-result-announcement,
    .result-label-container {
        @add-mixin visually-hidden;
    }
</style>

<div class="calculator {type}" on:keydown={handleKeydown} on:keyup={handleKeyup}>
    <div
        class="screen"
        class:mountfocus
        tabindex="0"
        role="textbox"
        bind:this={calculatorScreen}
        aria-label={!expression && !resultValue ? emptyInputFieldLabel : void 0}
        aria-labelledby={!expression && !resultValue ? void 0 : expressionContainerId}>
        <div class="expression" class:active id={expressionContainerId} aria-hidden="true">
            <MathExpressionTerm
                type="expression"
                value={expression}
                label={combineDigitTerms(addAriaLabelsToTerms(renderedTerms))} />
        </div>
        <div class="result" aria-hidden="true">
            <span class:error>
                {#if !active}
                    <span bind:this={resultContainer}>
                        <span class="result-label-container">{resultLabel}</span>
                        <MathExpressionTerm
                            type="result"
                            value={resultValue}
                            label={combineDigitTerms(addAriaLabelsToTerms(formattedResult))} />
                    </span>
                {/if}
            </span>
        </div>
    </div>
    <div
        class="keyboard"
        style={`--grid-rows-count: ${keyboard.length}`}
        bind:this={calculatorKeyboard}
        on:click={keyboardClick}
        on:keydown={handleKeyboardKeydown}>
        {#each keyboardButtons as { type, name, states, style, command, parameter, label, size, disabled, rowNum, colNum, keys }}
            {#if 'spacer' === type || disabled}
                <span class="spacer" style={getButtonGridPositionStyle(rowNum, colNum, size)} />
            {:else if 'switch' === type}
                <div class="switchContainer" style={getButtonGridPositionStyle(rowNum, colNum, size)}>
                    <RadioSwitch
                        {name}
                        options={getSwitchOptions(states)}
                        on:change={e => {
                            switchHandler(name, e);
                        }}
                        value={switchValues[name]} />
                </div>
            {:else}
                <button
                    type="button"
                    class="key {style}"
                    style={getButtonGridPositionStyle(rowNum, colNum, size)}
                    data-command={command}
                    data-param={parameter}
                    data-keys={getCompositeKey(keys)}
                    aria-label={getAriaLabelValue(label)}>
                    {#if 'icon' === getLabelType(label)}
                        <Icon name={getLabelValue(label)} />
                    {:else}
                        {@html getLabelValue(label)}
                    {/if}
                </button>
            {/if}
        {/each}
    </div>

    <span class="live-result-announcement" aria-live="polite" bind:this={politeLiveRegion} />
    <span class="live-result-announcement" aria-live="assertive" bind:this={assertiveLiveRegion} />
</div>
